import React, { useCallback } from 'react';

import { ScriptStatus, useScript } from '@lyearn/core/hooks/useScript';
import { useTranslation } from '@lyearn/i18n';
import { PinOutline } from '@lyearn/icons';
import { Input, RenderInputParams } from '@lyearn/ui';
import AsyncSelect from '@/components/AsyncSelect';
import { EventLocationInput, EventLocationTypeEnum } from '@/types/schema';

import useLocationSearch from '../hooks/useLocationSearch';
import { PlaceSuggestion } from '../types';
import { getGMapsLibraryUrl } from '../utils';
import { renderAutoCompleteOption } from './renderers';

interface LocationSearchProps<T> {
  location: EventLocationInput;
  onChange: (location: EventLocationInput) => void;
  disabled?: boolean;
}

const renderInput = (params: RenderInputParams) => (
  <Input
    {...params}
    endAdornment={<PinOutline className="h-24 w-24 me-4" fillColorToken="icon-placeholder" />}
  />
);

function LocationSearch<T>(props: LocationSearchProps<T>) {
  const { t } = useTranslation();
  const { location, onChange, disabled } = props;

  const [gmapsStatus] = useScript({ src: getGMapsLibraryUrl() });

  const getPlaceDetails = useCallback(
    (selectedPlace: PlaceSuggestion) => {
      if (gmapsStatus !== ScriptStatus.Success) {
        return null;
      }

      const geocoderService = new google.maps.Geocoder();
      geocoderService.geocode({ placeId: selectedPlace.id }).then(({ results }) => {
        if (results && results[0]) {
          onChange({
            ...location,
            type: EventLocationTypeEnum.GeoLocation,
            geoLocation: {
              addressLine1: selectedPlace.name,
              addressLine2: selectedPlace.description,
              latitude: results[0].geometry?.location?.lat(),
              longitude: results[0].geometry?.location?.lng(),
            },
          });
        }
      });
    },
    [gmapsStatus, location, onChange],
  );

  const _onChange = useCallback(
    async (selectedPlace: PlaceSuggestion | string) => {
      if (typeof selectedPlace !== 'string') {
        getPlaceDetails(selectedPlace);
      }
    },
    [getPlaceDetails],
  );

  const onInputChange = useCallback(
    (searchText: string) => {
      if (searchText !== location.geoLocation?.addressLine1) {
        onChange({
          ...location,
          customLocation: searchText,
          type: EventLocationTypeEnum.Custom,
        });
      }
    },
    [location, onChange],
  );

  return (
    <AsyncSelect
      disableClearable
      freeSolo
      className="flex-1"
      clearIcon={null}
      clearOnBlur={false}
      data-id="location-search"
      disabled={disabled}
      placeholder={t('Events:Editor.AddLocationPlaceholder')}
      popupIcon={null}
      queryParams={{ gmapsStatus }}
      renderInput={renderInput}
      renderOption={renderAutoCompleteOption}
      useOptionsQuery={useLocationSearch}
      value={
        (location.type === EventLocationTypeEnum.Custom
          ? location.customLocation
          : location.geoLocation?.addressLine1) || undefined
      }
      onChange={_onChange}
      onInputChange={onInputChange}
    />
  );
}

export default LocationSearch;
